import { useEffect } from 'react';
import type { JSX } from 'react';
import { injectHeadScript } from '../util/inject-head-script';
import {
    getAdvertisingWindow,
    type LintrkStub,
} from '../util/advertising-window';
import { isConfiguredPixelId } from './is-configured-pixel-id';
/* eslint-disable jsx-a11y/alt-text */

const LINKEDIN_SRC = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';

interface LinkedInTrackerProps {
    partnerId: string;
}

/**
 * Boot the LinkedIn Insight Tag.
 *
 * `insight.min.js` reads the partner id off `_linkedin_data_partner_ids`, so
 * that array has to be populated before the loader is injected.
 */
export const loadLinkedInInsightTag = (partnerId: string): void => {
    if (!isConfiguredPixelId(partnerId)) return;

    const w = getAdvertisingWindow();
    w._linkedin_partner_id = partnerId;
    const partnerIds = (w._linkedin_data_partner_ids ??= []);
    if (partnerIds.includes(partnerId)) return;
    partnerIds.push(partnerId);

    if (!w.lintrk) {
        function lintrk(action: string, data?: unknown): void {
            lintrk.q.push([action, data]);
        }
        lintrk.q = [] as unknown[][];
        w.lintrk = lintrk satisfies LintrkStub;
    }

    injectHeadScript(LINKEDIN_SRC);
};

/**
 * LinkedIn Insight Tag script half. The matching `<noscript><img>` half lives in
 * `LinkedInTrackerBody` and is SSR-rendered from `BodyPixels`.
 */
export const LinkedInTrackerHead: React.FC<LinkedInTrackerProps> = ({
    partnerId,
}) => {
    // Injected after hydration (see `injectHeadScript`).
    useEffect(() => {
        loadLinkedInInsightTag(partnerId);
    }, [partnerId]);

    return null;
};

export const LinkedInTrackerBody = ({
    partnerId,
}: LinkedInTrackerProps): JSX.Element => {
    return (
        <noscript>
            <img
                height="1"
                width="1"
                style={{ display: 'none' }}
                alt=""
                src={`https://px.ads.linkedin.com/collect/?pid=${partnerId}&fmt=gif`}
            />
        </noscript>
    );
};
